import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { EmployeeNetworkMaintenaince } from '@models/employee-network-maintenaince';
import { ScanAndWorkOrderFilters } from '@models/hierarchy/scan-and-work-order-filters';
import { TeamHierarchy } from '@models/hierarchy/team-hierarchy';
import { XmDateRange } from '@optek/xm-bootstrap';
import { UserService } from '@services/user.service';
import { Observable } from 'rxjs';
import { map, shareReplay } from 'rxjs/operators';
import { ObjectHelper } from '@utilities/object-helper';


@Injectable({
  providedIn: 'root'
})
export class MsoHierarchyService {

  constructor(private http: HttpClient, private userService: UserService) { }

  getSupervisorWithUsageStats(supervisorUsername: string, selectedDates: XmDateRange, filters: ScanAndWorkOrderFilters): Observable<EmployeeNetworkMaintenaince> {

    let fromDate = selectedDates.fromDate ? selectedDates.fromDate.getTime() : null;
    let toDate = selectedDates.toDate ? selectedDates.toDate.getTime() : null;

    const MILLIS_IN_DAY = 24 * 60 * 60 * 1000;

    /**
     * The toDate we get from the XmDateRange on calendar selection is the START of the day selected.
     * We are more interested in the END of the day select - so add the appropriate time onto toDate
     */
    if (toDate) {
      toDate += MILLIS_IN_DAY - 1;
    }

    const COLLECTION_URI = `/mso-hierarchy-svc/usage-stats-by-user`;
    let uriParams = `?user=${supervisorUsername}`;
    if (fromDate) {
      uriParams += `&fromTime=${fromDate}`;
    }
    if (toDate) {
      uriParams += `&toTime=${toDate}`;
    }
    let url = `${COLLECTION_URI}${uriParams}`;
    return this.http.post<EmployeeNetworkMaintenaince>(url, filters);
  }

  setUsageStatFilterPrefs(username: string, scanFilters: ScanAndWorkOrderFilters): Observable<ScanAndWorkOrderFilters> {
    const COLLECTION_URI = `/mso-hierarchy-svc/usage-stats-filters`;
    let url = `${COLLECTION_URI}/${username}`;
    return this.http.put<ScanAndWorkOrderFilters>(url, scanFilters);
  }

  getUsageStatFilterPrefs(username: string): Observable<ScanAndWorkOrderFilters> {
    const COLLECTION_URI = `/mso-hierarchy-svc/usage-stats-filters`;
    let url = `${COLLECTION_URI}/${username}`;
    return this.http.get<ScanAndWorkOrderFilters>(url).pipe(
      map(httpResponse => ObjectHelper.copyObject(httpResponse, ScanAndWorkOrderFilters)),
      map(scanAndWorkOrderFilters => {
        return scanAndWorkOrderFilters;
      })
    );
  }

  getInventoryStatListByUsername(supervisorUsername: string): Observable<EmployeeNetworkMaintenaince> {
    const COLLECTION_URI = `/mso-hierarchy-svc/inventoryStatsByUser`;
    let uriParams = `?user=${supervisorUsername}`;
    let url = `${COLLECTION_URI}${uriParams}`;
    return this.http.get<EmployeeNetworkMaintenaince>(url);
  }

  /**
   * Returns the employee information for the user provided and all their parents up to the top of the org
   * @returns Observable - TeamHierarchy[]
   */
  getEmployeeAndParents(userId: string): Observable<TeamHierarchy[]> {
    return this.http.get<TeamHierarchy[]>(`/mso-hierarchy-svc/getHierarchyLevelsForObject?objectId=${userId}`).pipe(
      shareReplay()
    );
  }

  /**
   * Returns the employee information for the direct reports (children) of the user provided
   * @returns Observable - TeamHierarchy[]
   */
  getEmployeeDirectReports(userId: string): Observable<TeamHierarchy[]> {
    return this.http.get<TeamHierarchy[]>(`/mso-hierarchy-svc/getHierarchyChildrenByObjectId?objectId=${userId}`);
  }
}
